import React from 'react';
import { FaGoogle, FaStar, FaRegStar } from 'react-icons/fa';
import reviewData from '@/public/storeReviews.json';

interface StoreReview {
    author: string;
    rating?: number;
    text: string;
    date: string;
}

interface StoreReviewEntry {
    store: string;
    googleUrl: string;
    reviews: StoreReview[];
}

const reviews = (reviewData as StoreReviewEntry[]).flatMap(({ store, googleUrl, reviews }) =>
    reviews.map((review) => ({ ...review, store, googleUrl }))
);

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
    <span className="inline-flex gap-0.5 text-[#FBBC04]" aria-label={`${rating} von 5 Sternen`}>
        {[1, 2, 3, 4, 5].map((i) => (i <= rating ? <FaStar key={i} aria-hidden="true" /> : <FaRegStar key={i} aria-hidden="true" />))}
    </span>
);

const StoreReviews: React.FC = () => {
    if (reviews.length === 0) return null;

    return (
        <section data-testid="store-reviews" className="bg-white py-8 lg:py-16">
            <div className="max-w-5xl mx-auto px-6">
                <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-center">Das sagen unsere Kund:innen</h2>
                <p className="text-lg text-gray-700 mb-10 text-center">Echte Bewertungen aus unseren Läden auf Google.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    {reviews.map((review, idx) => (
                        <blockquote key={idx} data-testid="store-review" className="bg-primaryLight rounded-xl shadow-sm p-6 flex flex-col gap-3 text-left">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="text-lg font-semibold">{review.store}</h3>
                                {review.rating && <Stars rating={review.rating} />}
                            </div>
                            <p className="text-gray-700 whitespace-pre-line">„{review.text}“</p>
                            <footer className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
                                <span>{review.author} · {review.date}</span>
                                <a
                                    href={review.googleUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 font-medium text-textColor hover:underline"
                                >
                                    <FaGoogle aria-hidden="true" />
                                    Auf Google ansehen
                                </a>
                            </footer>
                        </blockquote>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StoreReviews;
